import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react/dist/csr/ClockCounterClockwise';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';

import { Button } from '../../design-system/primitives/Button/Button';
import { Card } from '../../design-system/composites/Card/Card';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import { StatusPill } from '../../design-system/primitives/StatusPill/StatusPill';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  completeSaudiMobile,
  isCaseFreeTemplate,
  smsApi,
  type DirectMessageQuery,
  type SmsMessageRow,
  type SmsTemplate,
} from '../../lib/api/sms';
import { formatTimestamp } from '../../lib/format/dateTime';
import { mapDetails } from '../reference/referenceFormHelpers';
import { SMS_STATUS_PILL } from './smsStatus';
import styles from './DirectSmsPanel.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * Send SMS, "to a mobile number" mode — a message straight to a phone
 * number with NO case (product owner: "not all messages require a case
 * number to send a message. messages can also be sent to a phone number
 * directly"). `POST /sms-messages` to send, `GET /sms-messages` for the log
 * of what has been sent this way.
 *
 * ## The legacy screen this mirrors
 *
 * `callCentre-sendsms.php` was a mobile field and a message-kind picker and
 * nothing else — no case number for the informational messages. So is this:
 * one compact row (number, message, Send), the chosen message's text under
 * it, and the log below. It deliberately has NO free-text body: the old
 * screen never had one, and the template catalogue is the reviewed list of
 * what may go out under the company's sender ID.
 *
 * ## Only case-free templates are offered
 *
 * `isCaseFreeTemplate()` — a template declaring no variables. Every
 * placeholder the API can fill is read from a case, so the API refuses any
 * other template here with a `422` (`App\Support\Sms\TemplateRequiresCase`
 * in the API repository). Offering one only to have it refused is the "a
 * control that always fails" this codebase removes; the card's help line says
 * where the others live instead (send from the case). The `422` path is
 * still handled — `error.details.template_code` lands on the `Select` — for
 * a template edited between the catalogue loading and the send.
 *
 * ## The log follows the number being typed
 *
 * Unfiltered, the log is every case-less message the caller may see
 * (newest first, cursor-paged like every list here). Once the Mobile field
 * holds a COMPLETE Saudi number (`completeSaudiMobile()`), it narrows to
 * that number — "what has this caller already been sent?" is the question
 * an agent on the phone has before pressing Send, and answering it costs no
 * extra control. A half-typed number leaves the log alone rather than
 * refetching per keystroke.
 *
 * ## Success feedback is the new row at the top of the log
 *
 * The `202` row is prepended (the `CaseMessages.tsx` rule: the API already
 * answered the question a refetch would ask), carrying the same status pill
 * every message list renders. The mobile is kept — a second message to the
 * same caller is the common follow-up — and the template is kept too.
 */

interface DirectRow extends Row, SmsMessageRow {}

function toRow(message: SmsMessageRow): DirectRow {
  return { ...message, id: message.uuid };
}

export interface DirectSmsPanelProps {
  /** The active catalogue (`smsApi.templates()`), loaded once by the screen. */
  templates: readonly SmsTemplate[];
  /** The screen's "Send to" switch, placed in the compose card's head. */
  modeSwitch?: ReactNode;
}

export function DirectSmsPanel({ templates, modeSwitch }: DirectSmsPanelProps) {
  const { t, i18n } = useTranslation();

  const caseFree = templates.filter(isCaseFreeTemplate);

  const [mobile, setMobile] = useState('');
  const [templateCode, setTemplateCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly DirectRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const pages = useCursorPages();
  const { observe, first } = pages;
  const requestSeq = useRef(0);

  const filterMobile = completeSaudiMobile(mobile);

  // The first case-free template, once the catalogue arrives — a required
  // field that always has a value, the `SendMessageDialog` convention.
  const firstCode = caseFree[0]?.code ?? '';
  useEffect(() => {
    setTemplateCode((current) => (current === '' ? firstCode : current));
  }, [firstCode]);

  const load = useCallback(
    async (query: DirectMessageQuery) => {
      const seq = ++requestSeq.current;
      setStatus('loading');

      try {
        const page = await smsApi.direct(query);
        if (seq !== requestSeq.current) return;
        setRows(page.data.map(toRow));
        setTotal(page.meta.total);
        observe({ next_cursor: page.meta.next_cursor, total: page.meta.total });
        setStatus(page.data.length === 0 ? (query.mobile === undefined ? 'empty' : 'no-results') : 'ready');
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
        if (seq !== requestSeq.current) return;
        setStatus('error');
      }
    },
    [observe],
  );

  const query = useCallback((): DirectMessageQuery => {
    const built: DirectMessageQuery = { per_page: pages.perPage };
    if (filterMobile !== null) built.mobile = filterMobile;
    if (pages.cursor !== null) built.cursor = pages.cursor;
    return built;
  }, [filterMobile, pages.cursor, pages.perPage]);

  // A newly complete (or newly incomplete) number starts the log over from
  // its first page — a cursor minted for one filter means nothing to another.
  useEffect(() => {
    first();
  }, [filterMobile, first]);

  useEffect(() => {
    void load(query());
  }, [load, query]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('cases.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('cases.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('cases.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    // The one check made before the round trip: an empty field. Shape is
    // the API's to judge (`StoreDirectMessageRequest`), and its `422` lands
    // on the same field below.
    if (mobile.trim() === '') {
      setFieldErrors({ mobile: t('sendSms.mobileRequired') });
      return;
    }
    if (templateCode === '') return;

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    try {
      const sent = await smsApi.sendDirect(mobile, templateCode);
      setRows((current) => [toRow(sent), ...current.filter((row) => row.uuid !== sent.uuid)]);
      setTotal((current) => (current === null ? null : current + 1));
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const options: readonly SelectOption[] = caseFree.map((template) => ({
    value: template.code,
    label: template.name,
  }));

  const selected = caseFree.find((template) => template.code === templateCode) ?? null;

  function renderTemplate(row: Row): ReactNode {
    const message = row as unknown as SmsMessageRow;
    return message.template?.name ?? message.template?.code ?? t('sendSms.unknownTemplate');
  }

  function renderStatus(row: Row): ReactNode {
    const message = row as unknown as SmsMessageRow;
    return <StatusPill status={SMS_STATUS_PILL[message.status]} label={t(`sms.status.${message.status}`)} />;
  }

  function renderQueuedAt(row: Row): ReactNode {
    const message = row as unknown as SmsMessageRow;
    return <bdi dir="ltr">{formatTimestamp(message.queued_at, i18n.language)}</bdi>;
  }

  const columns: readonly Column[] = [
    { key: 'recipient', label: t('table.mobile'), neverMirrors: true },
    { key: 'template', label: t('sms.template'), render: renderTemplate },
    { key: 'status', label: t('table.status'), render: renderStatus },
    { key: 'queued_at', label: t('sendSms.queuedAt'), render: renderQueuedAt },
  ];

  return (
    <div className={styles['panel']} data-testid="directSmsPanel">
      {/* The compose card: the page's primary card, so it carries the wash.
          Number, template and Send sit on ONE subgrid row (see the sheet),
          so the three line up on the control baseline whatever the label,
          hint or error lengths. */}
      <Card
        wash
        icon={<PaperPlaneTiltIcon size={16} />}
        title={t('sendSms.composeTitle')}
        aside={modeSwitch}
        data-testid="directSmsCompose"
      >
        <form
          className={styles['compose']}
          method="post"
          noValidate
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          <Input
            label={t('table.mobile')}
            width="fill"
            numeric
            inputMode="tel"
            autoComplete="off"
            required
            placeholder="05XXXXXXXX"
            value={mobile}
            error={fieldErrors['mobile']}
            onChange={(event) => {
              setMobile(event.target.value);
            }}
          />

          <Select
            label={t('sms.template')}
            width="fill"
            required
            options={options}
            value={templateCode}
            disabled={options.length === 0}
            error={fieldErrors['template_code']}
            onChange={(event) => {
              setTemplateCode(event.target.value);
            }}
          />

          <div className={styles['sendCell']}>
            <Button
              type="submit"
              loading={submitting}
              disabled={options.length === 0}
              icon={<PaperPlaneTiltIcon size={16} />}
            >
              {t('sms.send')}
            </Button>
          </div>
        </form>

        <p className={styles['help']}>{t('sendSms.caseOnlyHint')}</p>

        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        {selected === null ? null : (
          <figure className={styles['previewBlock']}>
            <figcaption className={styles['previewLabel']}>{t('sendSms.previewLabel')}</figcaption>
            {/* `auto`: a case-free body may be Arabic or English, and its own
                first strong character decides, not the interface language. */}
            <p className={styles['preview']} dir="auto" lang={selected.locale}>
              {selected.body}
            </p>
          </figure>
        )}
      </Card>

      <Card
        flush
        icon={<ClockCounterClockwiseIcon size={16} />}
        title={
          filterMobile === null ? (
            t('sendSms.historyTitle')
          ) : (
            <>
              {t('sendSms.historyForMobile')} <bdi dir="ltr">{filterMobile}</bdi>
            </>
          )
        }
      >
        <DataTable
          caption={t('sendSms.historyTitle')}
          columns={columns}
          rows={rows}
          status={status}
          empty={{ body: t('sendSms.noHistory') }}
          noResults={{ title: t('sendSms.noHistoryForMobile') }}
          onClearFilters={() => {
            setMobile('');
          }}
          error={{ title: t('sendSms.historyLoadFailed') }}
        />
      </Card>

      <Pagination pages={pages} total={total} rowsOnPage={rows.length} />

      {status === 'error' ? (
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              void load(query());
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('actions.retry')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
